import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, CheckCircle2, Clock, XCircle, Upload, FileText, AlertCircle, Camera } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const VerificationPage = () => {
  const { user } = useAuth();
  const [verifications, setVerifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const fileInputRef = useRef(null);

  const verificationType = [
    {
      type: 'id',
      label: 'ID Verification',
      description: 'Upload a government-issued ID (passport, driver\'s license)',
      icon: FileText,
      required: true
    },
    {
      type: 'background_check',
      label: 'Background Check',
      description: 'Consent to background verification for safety',
      icon: Shield,
      required: false
    },
    {
      type: 'phone',
      label: 'Phone Verification',
      description: 'Verify your phone number via SMS',
      icon: FileText,
      required: false
    },
  ];

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const response = await api.get('/verification/status');
      setVerifications(response.data.verifications);
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Failed to fetch verification status:', err);
    } finally {
      setLoading(false);
    }
  };

  const getVerificationForType = (type) => {
    return verifications.find(v => v.verification_type === type);
  };

  const handleFileUpload = (type) => {
    setSelectedType(type);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'].includes(file.type)) {
      alert('Only JPG, PNG, and PDF files are allowed');
      return;
    }

    setUploading(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await api.post('/verification/submit', null, {
            params: {
              verification_type: selectedType,
              document_data: reader.result
            }
          });
          
          // Refresh status
          await fetchVerificationStatus();
          alert('Document submitted successfully! We\'ll review it within 24-48 hours.');
        } catch (err) {
          alert(err.response?.data?.detail || 'Failed to submit document');
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      alert('Failed to process file');
      setUploading(false);
    }
  };

  const getStatusBadge = (verification) => {
    if (!verification) {
      return (
        <Badge variant="outline" className="border-neutral-300">
          <AlertCircle className="h-3 w-3 mr-1" />
          Not Started
        </Badge>
      );
    }

    switch (verification.status) {
      case 'approved':
        return (
          <Badge variant="default" className="bg-success-100 text-success-700 border-success-300">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="default" className="bg-yellow-100 text-yellow-700 border-yellow-300">
            <Clock className="h-3 w-3 mr-1" />
            Under Review
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-bold text-primary-900 mb-2">
          Account Verification
        </h1>
        <p className="text-neutral-600">
          Verify your account to unlock all features and build trust with the community
        </p>
      </div>

      {/* Overall Status */}
      <Card className="border-2 border-primary-200 bg-gradient-to-r from-primary-50 to-secondary-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-primary-900 mb-1">
                Verification Progress
              </h3>
              <div className="flex items-center gap-4">
                <span className="text-sm text-neutral-700">
                  {summary?.approved || 0} of {verificationType.length} completed
                </span>
                {summary?.pending > 0 && (
                  <Badge variant="default" className="bg-yellow-100 text-yellow-700">
                    {summary.pending} pending review
                  </Badge>
                )}
              </div>
            </div>
            {user?.is_verified && (
              <Badge className="bg-success-500 text-white">
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Verified User
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Benefits */}
      <Alert>
        <CheckCircle2 className="h-4 w-4" />
        <AlertDescription>
          <strong>Why verify?</strong> Verified members can book events, connect with other travelers, and access premium features.
        </AlertDescription>
      </Alert>

      {/* Verification Types */}
      <div className="grid grid-cols-1 gap-6">
        {verificationType.map((vType) => {
          const verification = getVerificationForType(vType.type);
          const Icon = vType.icon;
          const isApproved = verification?.status === 'approved';
          const isPending = verification?.status === 'pending';
          const isRejected = verification?.status === 'rejected';

          return (
            <Card key={vType.type} className="border shadow-soft hover:shadow-md transition-all">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isApproved ? 'bg-success-100' : isPending ? 'bg-yellow-100' : 'bg-neutral-100'
                    }`}>
                      <Icon className={`h-6 w-6 ${
                        isApproved ? 'text-success-700' : isPending ? 'text-yellow-700' : 'text-neutral-500'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-xl">{vType.label}</CardTitle>
                        {vType.required && (
                          <Badge variant="outline" className="text-xs border-accent-300 text-accent-700">
                            Required
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="text-sm">
                        {vType.description}
                      </CardDescription>
                    </div>
                  </div>
                  {getStatusBadge(verification)}
                </div>
              </CardHeader>

              <CardContent>
                {isApproved && (
                  <div className="p-4 bg-success-50 border border-success-200 rounded-lg">
                    <div className="flex items-center gap-2 text-success-700">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">
                        Verified on {new Date(verification.verified_at || verification.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}

                {isPending && (
                  <div className="space-y-3">
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-2 text-yellow-800">
                        <Clock className="h-5 w-5 mt-0.5" />
                        <div>
                          <p className="font-medium mb-1">Under Review</p>
                          <p className="text-sm text-yellow-700">
                            Submitted on {new Date(verification.created_at).toLocaleDateString()}. 
                            We typically review documents within 24-48 hours.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isRejected && (
                  <div className="space-y-3">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2 text-red-800">
                        <XCircle className="h-5 w-5 mt-0.5" />
                        <div>
                          <p className="font-medium mb-1">Verification Rejected</p>
                          {verification.rejection_reason && (
                            <p className="text-sm text-red-700 mb-2">
                              Reason: {verification.rejection_reason}
                            </p>
                          )}
                          <p className="text-sm text-red-700">
                            You can resubmit with updated documents.
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleFileUpload(vType.type)}
                      disabled={uploading}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Resubmit Document
                    </Button>
                  </div>
                )}

                {!verification && (
                  <div className="space-y-3">
                    <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
                      <p className="text-sm text-neutral-700">
                        <strong>Accepted formats:</strong> JPG, PNG, PDF (max 10MB)
                      </p>
                      {vType.type === 'id' && (
                        <ul className="text-sm text-neutral-600 mt-2 space-y-1 list-disc list-inside">
                          <li>Government-issued photo ID</li>
                          <li>Passport, driver's license, or national ID</li>
                          <li>Photo must be clear and readable</li>
                        </ul>
                      )}
                    </div>
                    <Button
                      onClick={() => handleFileUpload(vType.type)}
                      disabled={uploading}
                      variant={vType.required ? 'default' : 'outline'}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading && selectedType === vType.type ? 'Uploading...' : 'Upload Document'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Help Section */}
      <Card className="bg-neutral-50 border-neutral-200">
        <CardHeader>
          <CardTitle className="text-lg">Need Help?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-neutral-700 space-y-2">
          <p>
            <strong>How long does verification take?</strong> We typically review documents within 24-48 hours.
          </p>
          <p>
            <strong>What if my document is rejected?</strong> You'll receive specific feedback and can resubmit corrected documents.
          </p>
          <p>
            <strong>Is my data secure?</strong> Yes, all documents are encrypted and stored securely. We never share your personal information.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerificationPage;
